import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { shuffle } from "../../../../utils/shuffle";

const randInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
const pickOne = (arr) => arr[randInt(0, arr.length - 1)];

const PROGRESSION_PATTERNS = [
  { step: 1, minMiddle: 3, maxMiddle: 12, deltas: [-1, 0, 1] },
  { step: 2, minMiddle: 6, maxMiddle: 20, deltas: [-1, 0, 1, 2] },
  { step: 5, minMiddle: 15, maxMiddle: 60, deltas: [-2, -1, 0, 1, 2] },
  { step: 10, minMiddle: 30, maxMiddle: 80, deltas: [-2, -1, 0, 1, 2] },
];

function buildKnownNumbers(middle, step, count) {
  const first = middle - Math.floor((count - 1) / 2) * step;
  return Array.from({ length: count }, (_, index) => first + index * step);
}

function buildDistractors(correct, mean, middle, totalCount) {
  const candidates = [
    correct - 1,
    correct + 1,
    correct - 2,
    correct + 2,
    correct - 7,
    correct + 7,
    mean,
    middle,
    totalCount * mean,
    Math.max(1, correct + 5),
    Math.max(1, correct - 5),
  ];

  const set = new Set();
  const add = (value) => {
    if (!Number.isFinite(value)) return;
    if (!Number.isInteger(value)) return;
    if (value <= 0 || value === correct) return;
    set.add(value);
  };

  candidates.forEach(add);

  let offset = 3;
  while (set.size < 3) {
    add(correct + offset);
    add(correct - offset);
    offset += 3;
  }

  return shuffle(Array.from(set)).slice(0, 3);
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 29:
 *   średnia liczb 1,2,3,4,5,x,y wynosi 3.  x+y = 7·3 − 15 = 6  (C).
 */
function createMay2025OriginalTask() {
  const knownNumbers = [1, 2, 3, 4, 5];
  const knownSum = knownNumbers.reduce((sum, value) => sum + value, 0);
  const mean = 3;
  const correct = 7 * mean - knownSum;
  const options = [4, 5, 6, 7].map((value, index) => ({
    value,
    latex: String(value),
    isCorrect: value === correct,
    label: String.fromCharCode(65 + index),
  }));

  return {
    type: "MaturaMeanSevenXYSumClosedTask",
    data: {
      knownNumbers,
      knownSum,
      mean,
      correct,
      middle: 3,
      step: 1,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 29,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  let knownNumbers;
  let mean;
  let correct;
  let middle;
  let step;
  let totalCount;

  for (let tries = 0; tries < 100; tries += 1) {
    const pattern = pickOne(PROGRESSION_PATTERNS);
    const knownCount = randInt(4, 7);
    totalCount = knownCount + 2;
    step = pattern.step;
    middle = randInt(pattern.minMiddle, pattern.maxMiddle);
    const delta = pickOne(pattern.deltas);
    mean = middle + delta;
    knownNumbers = buildKnownNumbers(middle, step, knownCount);
    const knownSum = knownNumbers.reduce((acc, value) => acc + value, 0);
    correct = totalCount * mean - knownSum;

    if (knownNumbers.every((value) => value > 0) && correct > 0 && correct <= 160) {
      break;
    }
  }

  const knownSum = knownNumbers.reduce((acc, value) => acc + value, 0);
  totalCount = knownNumbers.length + 2;
  const wrongValues = buildDistractors(correct, mean, middle, totalCount);
  const allValues = shuffle([correct, ...wrongValues]);
  const options = allValues.map((value, idx) => ({
    value,
    latex: String(value),
    isCorrect: value === correct,
    label: String.fromCharCode(65 + idx),
  }));
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaMeanSevenXYSumClosedTask",
    data: {
      knownNumbers,
      knownSum,
      mean,
      correct,
      middle,
      step,
      totalCount,
      options,
      correctIndex,
    },
  };
}

const TOTAL_COUNT_WORDS = {
  6: "sześciu",
  7: "siedmiu",
  8: "ośmiu",
  9: "dziewięciu",
};

export function renderTask({ data }) {
  const { knownNumbers, mean, options } = data;
  const totalCount = knownNumbers.length + 2;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Średnia arytmetyczna {TOTAL_COUNT_WORDS[totalCount] || totalCount} liczb: {" "}
        {knownNumbers.map((number, index) => <React.Fragment key={index}><InlineMath math={String(number)} />,{" "}</React.Fragment>)}
        <InlineMath math="x" />, <InlineMath math="y" /> jest równa {" "}
        <InlineMath math={String(mean)} />.
      </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        Suma <InlineMath math={"x+y"} /> jest równa
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    !!userAnswer &&
    typeof userAnswer.choiceIndex === "number" &&
    userAnswer.choiceIndex === data.correctIndex
  );
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />;
}

export function renderExplanation({ data }) {
  const { knownNumbers, knownSum, mean, correct } = data;
  const totalCount = knownNumbers.length + 2;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z definicji średniej arytmetycznej.
      </p>

      <p>Oznaczmy sumę znanych liczb przez <InlineMath math="S" />.</p>
      <BlockMath math={`S=${knownSum}`} />
      <BlockMath math={`\\frac{S+x+y}{${totalCount}}=${mean}`} />

      <p>
        Mnożymy obie strony równania przez <InlineMath math={String(totalCount)} />:
      </p>

      <BlockMath math={`${knownSum} + x + y = ${totalCount} \\cdot ${mean}`} />

      <p>
        <strong>Krok 2.</strong> Obliczamy prawą stronę i odejmujemy sumę znanych liczb.
      </p>

      <BlockMath math={`${knownSum} + x + y = ${totalCount * mean}`} />
      <BlockMath math={`x + y = ${totalCount * mean} - ${knownSum} = ${correct}`} />

      <p>
        Zatem <InlineMath math={`x+y=${correct}`} />.
      </p>
    </div>
  );
}
